/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React, { useState, useEffect, useContext } from "react"
import tw, { GlobalStyles, css } from "twin.macro"

import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import Header from "./header"
import Footer from "./footer"
import ModalRoot from "./modals/ModalRoot"
import ISITray from "./isi-tray"
import ISIStatic from "./isi-static"
import { useQueryParam, NumberParam } from "use-query-params"
import { myContext } from "../context/Provider"
import { Cookies } from "react-cookie-consent"
import { useLocation } from "@reach/router"
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies"
import {
  pushDatalayerEvent,
  resetScroll,
  stopScroll,
  useWindowSize,
} from "../utils"
import { useInView } from "react-intersection-observer"
import useScrollbarSize from "react-scrollbar-size"
import { Container } from "./auvelity-ui"
import createModal from "../components/modals/createModal"
import SavingsCardModal from "../components/savingsCardModal"
import interactiveIcon from "../images/icons/interactive-icon.svg"


const Layout = ({ children }) => {
  const { width } = useWindowSize()
  const scrollbarWidth = useScrollbarSize()
  const context = useContext(myContext)
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "-140px",
  })

  const [isiState] = useQueryParam("isistate", NumberParam)
  const [cookieVisible, setCookieVisible] = useState(false)
  const [activeParent, setActiveParent] = useState("")
  const [hideTray, setHideTray] = useState(false)
  const [isiTrayHeight, setISITrayHeight] = useState()
  const [renderStaticISI, setRenderStaticISI] = useState(true)
  const [windowScrollPosition, setWindowScrollPosition] = useState(0)
  const location = useLocation()
  const [showModal] = useQueryParam("showModal", NumberParam)

  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  //closing safety bar and mobile nav on initial page load
  useEffect(() => {
    if (!Cookies.get("axs-con")) {
      Cookies.set("axs-con", true, { expires: 30 })
      initializeAndTrack(location)
      pushDatalayerEvent({event: "gatsbyRouteChange"})
    }
    
    context.setSafetyOpen(false)
    context.setMobileMenu(false)
    const isiTray = document.getElementById("isiTray")
    const isiHeight = isiTray ? isiTray.offsetHeight : 0
    setISITrayHeight(isiHeight)

    if (isiState === 3) {
      setHideTray(true)
      setRenderStaticISI(false)
    } else {
      if (!context.firstLoad) {
        context.setFirstLoad(true)
      }
    }
  }, [])

  useEffect(() => {
    context.setIsMobile(width < 1024 ? true : false)
  }, [width])

  //locking and unlocking scroll as mobile menu or isi tray are toggled
  useEffect(() => {
    const top = window.scrollY
    const header = document.getElementById("header")
    const body = document.querySelector("body")
    if (context.safetyOpen || context.mobileMenu) {
      stopScroll(top, scrollbarWidth.width)
      if (context.mobileMenu) {
        header.style.top = `${top}px`
      }
    } else if (!context.safetyOpen && !context.mobileMenu) {
      resetScroll()
      header.style.removeProperty("top")
    }
  }, [context.safetyOpen, context.mobileMenu])

  //Track tab key
  useEffect(() => {
    const isFocusedElementHidden = e => {
      //get tab key
      const activeElement = document.activeElement

      if (e.key === "Tab") {
        const isiTray = document.getElementById("isiTray")
        if (isiTray) {
          const isiTop = isiTray.getBoundingClientRect().top

          const activeElementBottom =
            activeElement.getBoundingClientRect().bottom

          const offset = activeElementBottom - isiTop

          //if the tab key is going to more content, append tabindex to h1
          if (activeElement) {
            if (
              offset > -10 &&
              !activeElement.classList.contains("gtm-ISI-tray")
            ) {
              const inTray = activeElement.closest("#isiTray")
              if (!inTray) {
                // if the offset is more than 0, and the active element isn't the isi tray
                const position = window.scrollY + offset + isiTrayHeight
                window.scrollTo(0, position)
              }
            }
          }
        }
        if (activeElement.closest("#header")) {
          // if the active element is in the header, scroll to the top of the page.
          // and enable the header
          window.scrollTo(0, 0)
          context.setDisableHeader(false)
        } else if (activeElement.closest("#sticky-panel")) {
          // if the active element is the sticky panel scroll to top of sticky panel
          window.scrollTo(
            0,
            window.scrollY +
              activeElement.closest("#sticky-panel").getBoundingClientRect().top
          )
        } else if (!activeElement.closest("#header")) {
          // if the element isn't in the header, disable the header until it shows
          // up in the tab order.
          context.setDisableHeader(true)
        } else {
          // and just in case none of this is true, reenable the header and get out
          context.setDisableHeader(false)
          return
        }
      } else if (e.key === "Escape") {
        // using escape to close the submenu
        const subMenu = activeElement.closest(".subNav")
        if (!subMenu) {
          return
        } else if (subMenu) {
          const subMenuParent = subMenu.previousElementSibling
          subMenuParent.focus()
        }
      }
    }

    window.addEventListener("keyup", isFocusedElementHidden)

    return () => {
      window.removeEventListener("keyup", isFocusedElementHidden)
    }
  })

  useEffect(() => {
    if (inView) {
      document.body.classList.add('isi-in-view');
    } else {
      document.body.classList.remove('isi-in-view');
    }
  }, [inView]);

  return (
    <>
      <GlobalStyles />
      <a
        href="#main"
        tw="border-white absolute z-50 left-1/2 transform -translate-x-1/2 bg-darkgrey text-white capitalize px-3 py-2 rounded -translate-y-full transition focus-visible:(translate-y-0 mt-2)"
      >
        Skip to main content
      </a>

      <Header
        siteTitle={data.site.siteMetadata?.title || `Title`}
        cookieVisible={cookieVisible}
        activeParent={activeParent}
        setActiveParent={setActiveParent}
      />

      <main tw="relative" id="main">
        <div
          css={[
            (context.safetyOpen && isiState !== 2 && tw`invisible`) ||
              (isiState === 2 && tw`hidden`),
          ]}
        >
          {children}
        </div>

        {/* <ISIContent /> */}
        {!inView && !hideTray ? (
          <ISITray
            language={context.activeSpanishPage === true ? "spanish" : ""}
          />
        ) : isiState === 2 ? (
          <ISITray
            language={context.activeSpanishPage === true ? "spanish" : ""}
          />
        ) : null}

        <div ref={ref} css={[context.safetyOpen && tw`invisible`]}>
          <ISIStatic
            language={context.activeSpanishPage === true ? "spanish" : ""}
          />
        </div>
      </main>
      <div
        css={[
          (context.safetyOpen && isiState !== 2 && tw`invisible`) ||
            (isiState === 2 && tw`hidden`),
        ]}
      >
        <Footer />
      </div>
      <ModalRoot />
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
